import { useState } from 'react';
import type { QueryKey } from '@tanstack/react-query';
import type { CliActionResult } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { WizardDialog } from '../../components/WizardDialog';
import { Button, ErrorNotice, Field, Input } from '../../components/ui';

interface SsidRow {
  port: string;
  name: string | null;
  hidden: boolean | null;
}

/**
 * WiFi on an OMCI-managed ONU, edited through the chassis.
 *
 * Reads shipped long ago; this is the write half, ZTE-only because the
 * hardware is (a Huawei OLT manages SSIDs over TR-069). Only what the
 * operator actually touched is sent — an untouched field is not a write —
 * and the preview shows the exact commands with passphrases already masked;
 * the real key goes to the chassis and nowhere else.
 */
export function WifiEditDialog({
  oltId,
  onuId,
  radioOn,
  ssids,
  onClose,
  href,
  invalidate,
}: {
  oltId: string;
  onuId: string;
  radioOn: boolean | null;
  ssids: SsidRow[];
  onClose: () => void;
  /** Where the background toast/tray row links — the ONU's own page. */
  href?: string;
  /** Extra query keys to refresh once the write lands (the ONU page's own). */
  invalidate?: QueryKey[];
}) {
  const tasks = useTasks();
  const [radio24, setRadio24] = useState<'' | 'on' | 'off'>('');
  const [radio5g, setRadio5g] = useState<'' | 'on' | 'off'>('');
  const [names, setNames] = useState<Record<string, string>>({});
  const [keys, setKeys] = useState<Record<string, string>>({});
  const [hides, setHides] = useState<Record<string, '' | 'show' | 'hide'>>({});
  const [error, setError] = useState<string | null>(null);

  const body = (dryRun: boolean) => {
    const ssidChanges = ssids
      .map((s) => {
        const change: Record<string, unknown> = { port: s.port };
        if (names[s.port] !== undefined && names[s.port]!.trim() !== (s.name ?? '')) {
          change.name = names[s.port]!.trim();
        }
        if (keys[s.port]) change.key = keys[s.port];
        if (hides[s.port]) change.hide = hides[s.port] === 'hide';
        return change;
      })
      .filter((c) => Object.keys(c).length > 1);
    return {
      dryRun,
      ...(radio24 ? { radio24: radio24 === 'on' } : {}),
      ...(radio5g ? { radio5g: radio5g === 'on' } : {}),
      ...(ssidChanges.length > 0 ? { ssids: ssidChanges } : {}),
    };
  };

  const hasChanges = () => {
    const b = body(true);
    return 'radio24' in b || 'radio5g' in b || 'ssids' in b;
  };

  const path = `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/wifi`;


  /**
   * Write hands the OMCI change to the background tray and closes — like Resync.
   * The dry-run preview above stays in the dialog; only the write moves
   * off-screen, so the operator is not pinned while clients reconnect.
   */
  const startApply = () => {
    const payload = body(false);
    onClose();
    tasks.start<CliActionResult>({
      label: `WiFi over OMCI — ${onuId}`,
      ...(href ? { href } : {}),
      run: () => api.post<CliActionResult>(path, payload),
      done: (r) =>
        r.warnings?.length
          ? 'WiFi written over OMCI, with warnings. Clients on a renamed or re-keyed SSID reconnect by hand.'
          : 'WiFi written to the ONU over OMCI. Clients on a renamed or re-keyed SSID reconnect by hand.',
      invalidate: [['onu', onuId, 'omci-config'], ...(invalidate ?? [])],
    });
  };

  return (
    <WizardDialog
      title="Edit WiFi over OMCI"
      subtitle={`Written through the OLT — no TR-069 needed. Only the fields you change are sent${
        radioOn === false ? '; the 2.4G radio is currently off' : ''
      }.`}
      maxWidth="2xl"
      // One step, straight to Apply (operator, 2026-09-19: no previews of
      // chassis commands anywhere; the tray says what landed).
      steps={[{ key: 'configure', title: 'Configure' }]}
      currentStep={0}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!hasChanges()} onClick={startApply}>
            Apply
          </Button>
        </div>
      }
    >
      {error && <ErrorNotice error={error} />}

      <div className="grid gap-4 sm:grid-cols-2">
            <Field label="2.4G radio" hint="Leave as is unless you mean it — off drops every client">
              <select className="input" value={radio24} onChange={(e) => { setRadio24(e.target.value as never); }}>
                <option value="">Leave as is</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </Field>
            <Field label="5G radio" hint="Not every model has one">
              <select className="input" value={radio5g} onChange={(e) => { setRadio5g(e.target.value as never); }}>
                <option value="">Leave as is</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </Field>
          </div>

          {ssids.map((s) => (
            <div key={s.port} className="rounded-lg border border-line p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-mono text-xs text-muted">{s.port}</span>
                {s.name && <span className="text-xs text-faint">currently “{s.name}”</span>}
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="SSID name">
                  <Input
                    value={names[s.port] ?? s.name ?? ''}
                    maxLength={32}
                    placeholder="Leave as is"
                    onChange={(e) => { setNames((n) => ({ ...n, [s.port]: e.target.value })); }}
                  />
                </Field>
                <Field label="New passphrase" hint="Blank keeps the current one">
                  <Input
                    type="password"
                    value={keys[s.port] ?? ''}
                    maxLength={63}
                    placeholder="8–63 characters"
                    onChange={(e) => { setKeys((k) => ({ ...k, [s.port]: e.target.value })); }}
                  />
                </Field>
                <Field label="Broadcast">
                  <select
                    className="input"
                    value={hides[s.port] ?? ''}
                    onChange={(e) => { setHides((h) => ({ ...h, [s.port]: e.target.value as never })); }}
                  >
                    <option value="">Leave as is</option>
                    <option value="show">Visible</option>
                    <option value="hide">Hidden</option>
                  </select>
                </Field>
              </div>
            </div>
          ))}

    </WizardDialog>
  );
}
